import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Star, ChevronRight, BadgeCheck } from "lucide-react";
import { CareShell } from "@/components/CareShell";
import api from "@/lib/api";

function initials(name = "") {
  return name.replace(/[^A-Za-z ]/g, "").trim().split(" ").filter(Boolean).slice(-1)[0]?.[0] || "D";
}

export default function Specialists() {
  const navigate = useNavigate();
  const [specialists, setSpecialists] = useState([]);
  const [specialties, setSpecialties] = useState([]);
  const [filter, setFilter] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([api.get("/care/specialists"), api.get("/care/meta")])
      .then(([s, m]) => {
        setSpecialists(s.data || []);
        setSpecialties(m.data.specialties || []);
      })
      .finally(() => setLoading(false));
  }, []);

  const shown = filter ? specialists.filter((s) => s.specialty === filter) : specialists;

  return (
    <CareShell active="home" title="Specialists" showBack onBack={() => navigate("/care")}>
      <p className="text-[#A1A1AA]">Browse specialists and view their profile before you start a review.</p>

      <select
        data-testid="specialists-filter"
        value={filter}
        onChange={(e) => setFilter(e.target.value)}
        className="mt-4 w-full bg-[#1A1A1A] border border-[#333333] rounded-xl px-4 py-3.5 min-h-[44px] text-[#F8FAFC] focus:outline-none focus:border-[#EF9F27]"
      >
        <option value="">All specialties</option>
        {specialties.map((s) => (
          <option key={s} value={s}>{s}</option>
        ))}
      </select>

      {loading ? (
        <div className="mt-4 space-y-3">
          {[0, 1, 2].map((i) => (
            <div key={i} className="h-24 bg-[#1A1A1A] border border-[#333333] rounded-2xl animate-pulse" />
          ))}
        </div>
      ) : (
        <ul className="mt-4 space-y-3">
          {shown.map((s) => (
            <li key={s.id}>
              <button
                data-testid={`specialist-item-${s.id}`}
                onClick={() => navigate(`/care/specialists/${s.id}`)}
                className="w-full text-left bg-[#1A1A1A] border border-[#333333] rounded-2xl p-4 flex items-center gap-3 hover:bg-[#222222] transition-colors"
              >
                <div className="w-12 h-12 rounded-xl bg-[#EF9F27] text-[#111111] flex items-center justify-center font-head text-lg font-extrabold shrink-0">
                  {initials(s.name)}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="font-head font-bold text-[#F8FAFC] truncate flex items-center gap-1.5">
                    {s.name}
                    {s.verified && (
                      <span data-testid={`specialist-verified-${s.id}`} className="inline-flex items-center gap-0.5 text-[10px] font-bold text-[#22C55E] bg-[#22C55E]/15 px-1.5 py-0.5 rounded-full">
                        <BadgeCheck size={11} /> Verified
                      </span>
                    )}
                  </p>
                  <p className="text-sm text-[#EF9F27]">{s.specialty}</p>
                  <p className="text-xs text-[#A1A1AA] mt-0.5 flex items-center gap-2 flex-wrap">
                    <span className="inline-flex items-center gap-1">
                      <Star size={12} className="text-[#EF9F27] fill-[#EF9F27]" /> {s.rating != null ? s.rating : "New"}
                    </span>
                    <span>· {s.based}</span>
                    {s.fee != null && <span>· {s.fee} {s.currency || ""} / review</span>}
                  </p>
                </div>
                <ChevronRight className="text-[#666666] shrink-0" />
              </button>
            </li>
          ))}
        </ul>
      )}
    </CareShell>
  );
}
